import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { checkBusinessVerification, getUserProfile, publishCollabOffer } from '../../services/api';
import api from '../../services/api';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactQuill from '../common/RichTextEditor';
import { RadioGroup, Switch, FormField, Input, Select } from '../common/design-system/Field';
import Button from '../common/design-system/Button';
import 'react-quill/dist/quill.snow.css';
import { FothDateRangePicker } from '../common/DatePicker';
import {
  FaTrash, FaArrowLeft, FaPaperPlane, FaSave, FaCheck,
  FaUtensils, FaShoppingBag, FaPlaneDeparture, FaCalendarCheck, FaMusic, FaBed, FaDumbbell, FaSpa, FaGift, FaEllipsisH, FaMagic, FaLock, FaPlus, FaMinus
} from 'react-icons/fa';
import AICampaignAssistant from './AICampaignAssistant';
import { normalizeCountryId } from './AICampaignAssistant.utils';
import VerificationRequired from './VerificationRequired';
import FormErrorSummary from './FormErrorSummary';
import {
  scrollToAndFocusFirstError,
  scrollToAndFocusField,
  findStepForError,
  normalizeBackendErrors,
} from './collabValidationHelper';
import styles from './EditOffer.module.scss';

const TYPE_CHOICES = [
  { id: '1', name: 'types.1', icon: <FaUtensils /> },
  { id: '2', name: 'types.2', icon: <FaShoppingBag /> },
  { id: '3', name: 'types.3', icon: <FaPlaneDeparture /> },
  { id: '4', name: 'types.4', icon: <FaCalendarCheck /> },
  { id: '5', name: 'types.5', icon: <FaMusic /> },
  { id: '6', name: 'types.6', icon: <FaBed /> },
  { id: '7', name: 'types.7', icon: <FaDumbbell /> },
  { id: '8', name: 'types.8', icon: <FaSpa /> },
  { id: '9', name: 'types.9', icon: <FaPlaneDeparture /> },
  { id: '10', name: 'types.10', icon: <FaGift /> },
  { id: '11', name: 'types.11', icon: <FaEllipsisH /> }
];

const FOLLOWERS_REQUIREMENTS = [
  { id: '1', name: 'requirements.1' },
  { id: '2', name: 'requirements.2' },
  { id: '3', name: 'requirements.3' },
  { id: '4', name: 'requirements.4' }
];

const STATIC_EXCHANGE_OPTIONS = [
  { id: 1, code: '1', name: 'Post' }, { id: 2, code: '2', name: 'Story' }, { id: 3, code: '3', name: 'Reel' }
];
const STATIC_TARGET_OPTIONS = [
  { id: 1, code: '1', name: 'Lifestyle' }, { id: 2, code: '2', name: 'Fashion' }, { id: 3, code: '3', name: 'Beauty' },
  { id: 4, code: '4', name: 'Food' }, { id: 5, code: '5', name: 'Wellness' }, { id: 6, code: '6', name: 'Entertainment' },
  { id: 7, code: '7', name: 'Travel' }, { id: 8, code: '8', name: 'Sports' }, { id: 9, code: '9', name: 'Tech' }
];

const DELIVERABLES_MODE_OPTIONS = [
  {
    id: 'open',
    titleKey: 'create.form.deliverables_mode_open',
    title: 'Open deliverables',
    helpKey: 'create.form.deliverables_mode_open_help',
    help: 'Creators can publish the content they feel best represents the experience.'
  },
  {
    id: 'defined',
    titleKey: 'create.form.deliverables_mode_defined',
    title: 'Defined requirements',
    helpKey: 'create.form.deliverables_mode_defined_help',
    help: 'Set exact quantities for posts, stories, and reels.'
  }
];

const quillModules = {
  toolbar: {
    container: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      [{ 'align': [] }],
      ['clean']
    ]
  },
  clipboard: { matchVisual: false }
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'list', 'bullet', 'link', 'align'
];

const getOptionValue = (option) => String(option?.code ?? option?.id ?? option);

const hasPositiveDeliverableCount = (counts = {}) => (
  Object.values(counts || {}).some(count => Number.parseInt(count, 10) > 0)
);

const buildDefinedDeliverablesCounts = (selectedOptions = [], currentCounts = {}) => (
  selectedOptions.reduce((counts, optionId) => {
    const numericCount = Number.parseInt(currentCounts[optionId], 10);
    counts[optionId] = Number.isNaN(numericCount) ? 1 : Math.max(numericCount, 0);
    return counts;
  }, {})
);

const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/jpg'];
const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png'];

const isImageFileValid = (file) => {
  if (!file) return false;
  const mime = file.type ? file.type.toLowerCase() : '';
  const name = file.name ? file.name.toLowerCase() : '';
  const isMimeValid = ALLOWED_IMAGE_TYPES.includes(mime);
  const isExtValid = ALLOWED_IMAGE_EXTENSIONS.some(ext => name.endsWith(ext));
  return isMimeValid || isExtValid;
};

const parseDateOnly = (dateValue) => {
  if (!dateValue) return null;
  if (dateValue instanceof Date) return dateValue;
  const match = String(dateValue).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const formatLocalDate = (dateValue) => {
  if (!dateValue) return '';
  if (typeof dateValue === 'string') {
    const match = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return match[0];
  }
  const d = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const stepVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3, ease: 'easeInOut' } },
  exit: { opacity: 0, x: -20, transition: { duration: 0.2, ease: 'easeInOut' } },
};

const EditOffer = () => {
  const { id } = useParams();
  const { t } = useTranslation('collab');
  const { currentUser, isAuthenticated, refreshUser, subscriptionInfo } = useAuth();
  const navigate = useNavigate();

  const [offerPlanLimit, setOfferPlanLimit] = useState(null);
  const maxCreators = Number(
    offerPlanLimit?.max_creators_per_collab ??
    subscriptionInfo?.max_creators_per_collab ??
    subscriptionInfo?.active_subscription?.max_creators_per_collab ??
    0
  );
  const isUnlimitedCreators = Boolean(
    (offerPlanLimit?.is_unlimited) ||
    (subscriptionInfo?.has_active_subscription && maxCreators === 0)
  );

  const [isQuillLoaded, setIsQuillLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  const [featuredImagePreview, setFeaturedImagePreview] = useState(null);
  const [additionalImagesPreview, setAdditionalImagesPreview] = useState([]);
  const [existingAdditionalImages, setExistingAdditionalImages] = useState([]);

  const objectUrlsRef = useRef(new Set());
  const createTrackedObjectURL = useCallback((file) => {
    const url = URL.createObjectURL(file);
    objectUrlsRef.current.add(url);
    return url;
  }, []);

  const tRef = useRef(t);
  tRef.current = t;
  const loadedOfferIdRef = useRef(null);

  useEffect(() => {
    const urls = objectUrlsRef.current;
    return () => {
      urls.forEach(url => {
        if (typeof URL.revokeObjectURL === 'function') {
          URL.revokeObjectURL(url);
        }
      });
      urls.clear();
    };
  }, []);

  const [formData, setFormData] = useState({
    title: '',
    type: '',
    value: '',
    compensation_mode: 'exchange',
    cash_currency: 'USD',
    quantity: 1,
    country: '',
    followers_requirements: '1',
    from_date: new Date(),
    to_date: new Date(),
    description: '',
    exchange_options: [],
    deliverables_counts: {},
    deliverables_mode: 'defined',
    target_options: [],
    terms_and_conditions: '',
    sponsor_this_offer: false,
    sponsor_amount: '',
    featured_image: null,
    additional_images: [],
    add_time_range: false,
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [originalStatus, setOriginalStatus] = useState(null);
  const [quantityLockedByPayment, setQuantityLockedByPayment] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const isPaidCollab = ['paid', 'hybrid'].includes(formData.compensation_mode) || Boolean(formData.sponsor_this_offer);
  const totalInvestment = isPaidCollab
    ? ((Number(formData.sponsor_amount) || 0) * (Number(formData.quantity) || 0))
    : 0;

  const [countries, setCountries] = useState([]);
  const [exchangeOptionsList, setExchangeOptionsList] = useState([]);
  const [targetOptionsList, setTargetOptionsList] = useState([]);
  const [isAIAssistantOpen, setIsAIAssistantOpen] = useState(false);

  const getMaximumCampaignDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date;
  };

  const applyAIAssistantData = (data) => {
    const nextFromDate = parseDateOnly(data.from_date);
    const nextToDate = parseDateOnly(data.to_date);
    const resolvedCountry = normalizeCountryId(data.country, countries) || data.country;

    setIsDirty(true);
    setFormData(prev => ({
      ...prev,
      title: data.title || prev.title,
      value: data.value ?? prev.value,
      quantity: quantityLockedByPayment
        ? prev.quantity
        : (maxCreators > 0 && Number(data.quantity) > maxCreators ? maxCreators : (data.quantity || prev.quantity)),
      description: data.description ? (data.description.startsWith('<p>') ? data.description : `<p>${data.description}</p>`) : prev.description,
      type: data.type || prev.type,
      followers_requirements: data.followers_requirements || prev.followers_requirements,
      exchange_options: Array.isArray(data.exchange_options) && data.exchange_options.length ? data.exchange_options.map(String) : prev.exchange_options,
      deliverables_mode: data.deliverables_mode || prev.deliverables_mode,
      deliverables_counts: data.deliverables_mode === 'open' ? {} : (data.deliverables_counts && Object.keys(data.deliverables_counts).length ? data.deliverables_counts : prev.deliverables_counts),
      country: resolvedCountry || prev.country,
      from_date: nextFromDate && !Number.isNaN(nextFromDate.getTime()) ? nextFromDate : prev.from_date,
      to_date: nextToDate && !Number.isNaN(nextToDate.getTime()) ? nextToDate : prev.to_date,
      add_time_range: data.add_time_range !== undefined ? data.add_time_range : prev.add_time_range,
      target_options: Array.isArray(data.target_options) && data.target_options.length ? data.target_options.map(String) : prev.target_options,
      terms_and_conditions: data.terms_and_conditions ? (data.terms_and_conditions.startsWith('<p>') ? data.terms_and_conditions : `<p>${data.terms_and_conditions.replace(/\n/g, '<br/>')}</p>`) : prev.terms_and_conditions,
      sponsor_this_offer: data.sponsor_this_offer !== undefined ? data.sponsor_this_offer : prev.sponsor_this_offer,
      sponsor_amount: quantityLockedByPayment ? prev.sponsor_amount : (data.sponsor_amount || prev.sponsor_amount),
    }));
    setErrors({});
    toast.success(t('create.ai_success', '¡Datos aplicados desde la IA!'));
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!isAuthenticated || currentUser?.user_type !== 'business') {
        setLoading(false);
        return;
      }
      if (id && loadedOfferIdRef.current === id) {
        setLoading(false);
        return;
      }
      try {
        const [verification, countriesRes, profile, exchangeOpt, targetOpt, offerResponse] = await Promise.all([
          checkBusinessVerification().catch(() => ({ data: { is_verified: true } })),
          api.get('/countries/').catch(() => ({ data: [] })),
          getUserProfile().catch(() => ({ data: {} })),
          api.get('/collabs/exchange-options/').catch(() => ({ data: [] })),
          api.get('/collabs/target-options/').catch(() => ({ data: [] })),
          id && id !== 'new' ? api.get(`/collabs/collab/${id}/`) : Promise.resolve({ data: null }),
        ]);

        setVerificationStatus(verification.data);
        setCountries(countriesRes.data || []);
        setExchangeOptionsList(exchangeOpt.data?.length ? exchangeOpt.data : STATIC_EXCHANGE_OPTIONS);
        setTargetOptionsList(targetOpt.data?.length ? targetOpt.data : STATIC_TARGET_OPTIONS);

        if (offerResponse?.data) {
          loadedOfferIdRef.current = id;
          const offerData = offerResponse.data;
          setOriginalStatus(offerData.status);
          setOfferPlanLimit(offerData.plan_limit_info || null);

          const isPaymentFunded = ['paid', 'partially_refunded', 'fully_refunded', 'disputed'].includes(
            offerData.paid_collab_payment_status,
          );
          setQuantityLockedByPayment(isPaymentFunded);

          const isCollabLocked = offerData.can_edit === false || isPaymentFunded || offerData.status === 'cancelled';
          setIsLocked(isCollabLocked);
          if (isCollabLocked) {
            toast.info(tRef.current('summary.actions.edit_locked_help', 'Esta colaboración ya ha sido financiada o cuenta con entregables de creadores en curso. Por seguridad del acuerdo y cumplimiento contractual, sus términos principales no pueden ser alterados.'));
          }

          let offerType = offerData.type;
          if (['exchange', 'paid', 'hybrid'].includes(offerType)) offerType = '1';

          setFormData(prev => ({
            ...prev,
            title: offerData.title || '',
            description: offerData.description || '',
            type: String(offerType || ''),
            value: offerData.value || '',
            compensation_mode: offerData.compensation_mode || (offerData.sponsor_this_offer ? 'hybrid' : 'exchange'),
            cash_currency: offerData.cash_currency || 'USD',
            quantity: offerData.quantity || 1,
            country: String(offerData.country || profile?.data?.country || ''),
            followers_requirements: String(offerData.followers_requirements || '1'),
            from_date: parseDateOnly(offerData.from_date) || new Date(),
            to_date: parseDateOnly(offerData.to_date) || new Date(),
            add_time_range: offerData.add_time_range || false,
            exchange_options: Array.isArray(offerData.exchange_options)
              ? offerData.exchange_options.map(opt => typeof opt === 'object' ? String(opt.code) : String(opt))
              : [],
            deliverables_counts: offerData.deliverables_counts || {},
            deliverables_mode: offerData.deliverables_mode || 'defined',
            target_options: Array.isArray(offerData.target_options)
              ? offerData.target_options.map(opt => typeof opt === 'object' ? String(opt.code) : String(opt))
              : [],
            terms_and_conditions: offerData.terms_and_conditions || '',
            sponsor_this_offer: offerData.sponsor_this_offer || ['paid', 'hybrid'].includes(offerData.compensation_mode),
            sponsor_amount: offerData.sponsor_amount || '',
            featured_image: null,
            additional_images: []
          }));

          if (offerData.featured_image_url || offerData.featured_image) {
            setFeaturedImagePreview(offerData.featured_image_url || offerData.featured_image);
          }
          if (Array.isArray(offerData.additional_images)) {
            const initialAddImages = offerData.additional_images
              .map(img => (typeof img === 'string' ? img : (img?.url || img?.image || img?.file)))
              .filter(Boolean);
            setExistingAdditionalImages(initialAddImages);
            setAdditionalImagesPreview(initialAddImages);
          }
        }
      } catch (err) {
        toast.error(tRef.current('create.errors.load_failed', 'Failed to load offer data.'));
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [id, currentUser, isAuthenticated]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const handleImageChange = (e, type) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const validFiles = files.filter(isImageFileValid);
    if (validFiles.length !== files.length) {
      toast.error(t('create.errors.invalid_image_format', 'Solo se permiten imágenes en formato JPG o PNG.'));
    }
    if (!validFiles.length) {
      e.target.value = '';
      return;
    }

    setIsDirty(true);
    if (type === 'featured') {
      const file = validFiles[0];
      setFormData(prev => ({ ...prev, featured_image: file }));
      setFeaturedImagePreview(createTrackedObjectURL(file));
      if (errors.featured_image) {
        setErrors(prev => ({ ...prev, featured_image: null }));
      }
    } else {
      const newImages = [...formData.additional_images, ...validFiles];
      const limit = Math.max(0, 5 - existingAdditionalImages.length);
      const allowedImages = newImages.slice(0, limit);

      setFormData(prev => ({ ...prev, additional_images: allowedImages }));

      const existingPreviews = existingAdditionalImages;
      const newPreviews = allowedImages.map(file => createTrackedObjectURL(file));
      setAdditionalImagesPreview([...existingPreviews, ...newPreviews]);
    }
  };

  const handleRemoveImage = (index, type) => {
    setIsDirty(true);
    if (type === 'featured') {
      setFormData(prev => ({ ...prev, featured_image: null }));
      setFeaturedImagePreview(null);
    } else {
      if (index < existingAdditionalImages.length) {
        const updatedExisting = existingAdditionalImages.filter((_, i) => i !== index);
        setExistingAdditionalImages(updatedExisting);
        const newPreviews = [
          ...updatedExisting,
          ...formData.additional_images.map(file => createTrackedObjectURL(file))
        ];
        setAdditionalImagesPreview(newPreviews);
      } else {
        const fileIndex = index - existingAdditionalImages.length;
        const newImages = formData.additional_images.filter((_, i) => i !== fileIndex);
        setFormData(prev => ({ ...prev, additional_images: newImages }));
        const newPreviews = [
          ...existingAdditionalImages,
          ...newImages.map(file => createTrackedObjectURL(file))
        ];
        setAdditionalImagesPreview(newPreviews);
      }
    }
  };

  const isRichTextEmpty = (content) => {
    if (!content) return true;
    const textContent = content.replace(/<[^>]*>/g, '').trim();
    return textContent === '';
  };

  const validateStep = (stepToValidate = currentStep, shouldScroll = true) => {
    const newErrors = {};
    if (stepToValidate === 1) {
      if (!formData.title?.trim()) newErrors.title = t('create.errors.title_req', 'Title is required');
      if (!formData.type) newErrors.type = t('create.errors.type_req', 'Type is required');
      if (formData.compensation_mode !== 'paid' && !formData.value) newErrors.value = t('create.errors.budget_req', 'Value is required');
      if (formData.compensation_mode === 'paid' && (!formData.sponsor_amount || Number(formData.sponsor_amount) <= 0)) {
        newErrors.sponsor_amount = t('create.errors.paid_amount_req', 'Set a positive payment per creator.');
      }
      const qty = Number(formData.quantity);
      if (!formData.quantity || Number.isNaN(qty) || qty <= 0) {
        newErrors.quantity = t('create.errors.quantity_req', 'Indica un número válido de creadores (mínimo 1)');
      } else if (maxCreators > 0 && qty > maxCreators) {
        newErrors.quantity = t('create.errors.quantity_max', { count: maxCreators, defaultValue: `Tu plan permite hasta ${maxCreators} creadores por collab.` });
      }
      if (!formData.country) newErrors.country = t('create.errors.country_req', 'Country is required');
      if (isRichTextEmpty(formData.description)) newErrors.description = t('create.errors.desc_req', 'Description is required');

      if (!formData.from_date || !formData.to_date) {
        newErrors.dates = t('create.errors.dates_req', 'Start and end dates are required');
      } else if (formData.to_date <= formData.from_date) {
        newErrors.dates = t('create.errors.dates_order', 'End date must be after the start date');
      } else {
        const maxEndDate = new Date(formData.from_date);
        maxEndDate.setDate(formData.from_date.getDate() + 30);
        if (formData.to_date > maxEndDate) {
          newErrors.dates = t('create.errors.dates_max', 'The collab cannot last longer than 30 days');
        }
      }
    }
    if (stepToValidate === 2) {
      if (formData.deliverables_mode === 'defined') {
        if (formData.exchange_options.length === 0) newErrors.exchange_options = t('create.errors.exchange_req', 'Select at least one exchange option');
        const definedCounts = buildDefinedDeliverablesCounts(formData.exchange_options, formData.deliverables_counts);
        if (!hasPositiveDeliverableCount(definedCounts)) {
          newErrors.deliverables_counts = t('create.errors.deliverables_count_req', 'Add at least one deliverable quantity greater than zero.');
        }
      }
      if (formData.target_options.length === 0) newErrors.target_options = t('create.errors.target_req', 'Select at least one target option');
    }
    if (stepToValidate === 3) {
      if (!formData.featured_image && !featuredImagePreview) newErrors.featured_image = t('create.errors.image_req', 'Featured image is required');
      if (isRichTextEmpty(formData.terms_and_conditions)) newErrors.terms_and_conditions = t('create.errors.terms_req', 'Terms are required');
      if (formData.sponsor_this_offer && (!formData.sponsor_amount || Number(formData.sponsor_amount) <= 0)) {
        newErrors.sponsor_amount = t('create.errors.sponsor_amount_req', 'Sponsorship amount must be greater than 0.');
      }
    }
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    if (!isValid && shouldScroll) {
      scrollToAndFocusFirstError(newErrors, stepToValidate, 'edit-offer');
    }
    return isValid;
  };

  const validateAllSteps = () => {
    for (let s = 1; s <= 3; s++) {
      const stepValid = validateStep(s, false);
      if (!stepValid) {
        if (currentStep !== s) {
          setCurrentStep(s);
        }
        setTimeout(() => {
          validateStep(s, true);
        }, 120);
        return false;
      }
    }
    return true;
  };

  const safeScrollToTop = () => {
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      try {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } catch {
        window.scrollTo(0, 0);
      }
    }
  };

  const handleStepNavigation = (targetStep) => {
    if (targetStep === currentStep) return;
    if (targetStep < currentStep) {
      setErrors({});
      setCurrentStep(targetStep);
      safeScrollToTop();
    } else {
      if (validateStep(currentStep, true)) {
        setErrors({});
        setCurrentStep(targetStep);
        safeScrollToTop();
      }
    }
  };

  const handleNextStep = () => {
    if (validateStep(currentStep, true)) {
      setErrors({});
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      safeScrollToTop();
    }
  };

  const handlePreviousStep = () => {
    setErrors({});
    setCurrentStep(prev => Math.max(prev - 1, 1));
    safeScrollToTop();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setIsDirty(true);
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleRichTextChange = (content, fieldName) => {
    const cleanContent = content === '<p><br></p>' ? '' : content;
    setIsDirty(true);
    setFormData(prev => ({ ...prev, [fieldName]: cleanContent }));
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: null }));
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsQuillLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleDateRangeChange = ({ startDate, endDate }) => {
    setIsDirty(true);
    setFormData(prev => ({ ...prev, from_date: startDate, to_date: endDate }));
    if (errors.dates || errors.from_date || errors.to_date) {
      setErrors(prev => ({ ...prev, dates: null, from_date: null, to_date: null }));
    }
  };

  const handleMultiSelect = (name, optionId) => {
    setIsDirty(true);
    const currentSelection = formData[name];
    const isSelected = currentSelection.includes(optionId);
    const newSelection = isSelected
      ? currentSelection.filter(item => item !== optionId)
      : [...currentSelection, optionId];

    const newCounts = { ...formData.deliverables_counts };
    if (name === 'exchange_options') {
      if (isSelected) {
        delete newCounts[optionId];
      } else if (formData.deliverables_mode === 'defined') {
        newCounts[optionId] = 1;
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: newSelection,
      deliverables_counts: name === 'exchange_options' ? newCounts : prev.deliverables_counts
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleDeliverableCountChange = (optionId, count) => {
    setIsDirty(true);
    const val = Number.parseInt(count, 10);
    setFormData(prev => ({
      ...prev,
      deliverables_counts: {
        ...prev.deliverables_counts,
        [optionId]: Number.isNaN(val) ? 0 : Math.max(val, 0)
      }
    }));
    if (errors.deliverables_counts) {
      setErrors(prev => ({ ...prev, deliverables_counts: null }));
    }
  };

  const handleDeliverableCountIncrement = (optValue, delta) => {
    const current = Number.parseInt(formData.deliverables_counts[optValue] ?? 1, 10);
    const nextVal = Math.max(1, (Number.isNaN(current) ? 1 : current) + delta);
    handleDeliverableCountChange(optValue, nextVal);
  };

  const handleDeliverablesModeChange = (mode) => {
    setIsDirty(true);
    setFormData(prev => ({
      ...prev,
      deliverables_mode: mode,
      deliverables_counts: mode === 'defined'
        ? buildDefinedDeliverablesCounts(prev.exchange_options, prev.deliverables_counts)
        : {}
    }));
    setErrors(prev => ({ ...prev, exchange_options: null, deliverables_counts: null }));
  };

  const submitOfferData = async (statusMode = 'save') => {
    if (isLocked) {
      toast.info(t('summary.actions.edit_locked_help', 'Esta colaboración ya no permite modificaciones.'));
      return;
    }

    const isPublishingAction = statusMode === 'active';

    if (isPublishingAction && !validateAllSteps()) {
      toast.error(t('create.errors.publish_fill', 'Por favor completa los campos requeridos antes de guardar.'));
      return;
    }

    const loader = isPublishingAction ? setIsPublishing : setSubmitting;
    loader(true);

    const normalizedFormData = {
      ...formData,
      add_booking: false,
      sponsor_this_offer: formData.compensation_mode !== 'exchange' || Boolean(formData.sponsor_this_offer),
      value: formData.value,
      deliverables_counts: formData.deliverables_mode === 'defined'
        ? buildDefinedDeliverablesCounts(formData.exchange_options, formData.deliverables_counts)
        : {}
    };

    const data = new FormData();
    Object.keys(normalizedFormData).forEach(key => {
      if (key === 'additional_images') {
        normalizedFormData.additional_images.forEach(file => data.append('additional_images', file));
      } else if (Array.isArray(normalizedFormData[key])) {
        normalizedFormData[key].forEach(item => data.append(key, item));
      } else if (normalizedFormData[key] !== null && normalizedFormData[key] !== undefined) {
        if (normalizedFormData[key] instanceof Date) {
          data.append(key, formatLocalDate(normalizedFormData[key]));
        } else if (key === 'deliverables_counts') {
          data.append(key, JSON.stringify(normalizedFormData[key]));
        } else {
          data.append(key, normalizedFormData[key]);
        }
      }
    });

    data.append('retained_additional_images', JSON.stringify(existingAdditionalImages));

    try {
      await api.put(`/collabs/collab/${id}/`, data);

      if (isPublishingAction) {
        if (['paid', 'hybrid'].includes(formData.compensation_mode) || formData.sponsor_this_offer) {
          toast.info(t('payment_release.created_paid_help'));
          navigate(`/business/collabs/${id}/summary`);
          return;
        }
        try {
          await publishCollabOffer(id);
        } catch (publishError) {
          if (publishError.response?.status === 402) {
            toast.info(publishError.response?.data?.error || 'A subscription is required to publish this collab.');
            navigate(`/business/payment/required?offerId=${id}`);
            return;
          }
          throw publishError;
        }
      }

      toast.success(t('summary.toasts.saved', '¡Colaboración actualizada correctamente!'));
      setIsDirty(false);
      navigate(`/business/collabs/${id}/summary`);
      refreshUser();
    } catch (error) {
      const backendFieldErrors = normalizeBackendErrors(error.response?.data);
      if (Object.keys(backendFieldErrors).length > 0) {
        setErrors(backendFieldErrors);
        const targetStep = findStepForError(backendFieldErrors);
        if (currentStep !== targetStep) {
          setCurrentStep(targetStep);
        }
        setTimeout(() => {
          scrollToAndFocusFirstError(backendFieldErrors, targetStep, 'edit-offer');
        }, 150);
      }
      toast.error(error.response?.data?.error || error.response?.data?.detail || t('create.errors.update_failed', 'Error al actualizar la colaboración.'));
    } finally {
      loader(false);
    }
  };

  const renderProgressBar = () => {
    const stepTitles = [
      t('create.steps.details', 'Detalles'),
      t('create.steps.requirements', 'Requisitos'),
      t('create.steps.media', 'Media y Condiciones')
    ];

    return (
      <div className={styles.stepperNav}>
        {stepTitles.map((label, index) => {
          const stepNum = index + 1;
          const isActive = currentStep === stepNum;
          const isCompleted = currentStep > stepNum;

          return (
            <button
              type="button"
              key={label}
              className={`${styles.stepperTab} ${isActive ? styles.active : ''} ${isCompleted ? styles.completed : ''}`}
              onClick={() => handleStepNavigation(stepNum)}
            >
              <span className={styles.tabNum}>{stepNum}</span>
              <span>{label}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderFormStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div key="step1" variants={stepVariants} initial="hidden" animate="visible" exit="exit">
            <div className={styles.sectionHeading}>
              <h3>{t('create.steps.details', 'Detalles de la colaboración')}</h3>
              <p>{t('create.subtitle', 'Ajusta los parámetros principales de la campaña y sus creadores.')}</p>
            </div>

            <div className={styles.formGrid}>
              <div className={styles.colSpan2}>
                <FormField id="edit-offer-title" label={t('create.form.title_label')} error={errors.title}>
                  <Input
                    id="edit-offer-title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    disabled={isLocked}
                    placeholder={t('create.form.title_placeholder')}
                    className={errors.title ? 'error' : ''}
                  />
                </FormField>
              </div>

              <div>
                <FormField
                  id={['paid', 'hybrid'].includes(formData.compensation_mode) ? 'edit-offer-sponsor-amount' : 'edit-offer-value'}
                  label={t(['paid', 'hybrid'].includes(formData.compensation_mode) ? 'create.form.paid_amount_label' : 'create.form.budget_label')}
                  error={['paid', 'hybrid'].includes(formData.compensation_mode) ? errors.sponsor_amount : errors.value}
                >
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    id={['paid', 'hybrid'].includes(formData.compensation_mode) ? 'edit-offer-sponsor-amount' : 'edit-offer-value'}
                    name={['paid', 'hybrid'].includes(formData.compensation_mode) ? 'sponsor_amount' : 'value'}
                    value={['paid', 'hybrid'].includes(formData.compensation_mode) ? formData.sponsor_amount : formData.value}
                    onChange={handleChange}
                    disabled={isLocked || quantityLockedByPayment}
                    placeholder={t('create.form.budget_placeholder')}
                    className={errors.sponsor_amount || errors.value ? 'error' : ''}
                  />
                </FormField>
              </div>

              {['paid', 'hybrid'].includes(formData.compensation_mode) && (
                <div>
                  <FormField id="edit-offer-in-kind-value" label={t('create.form.in_kind_value_label')} hint={t('create.form.in_kind_value_help')} error={errors.value}>
                    <div className={styles.moneyInputGroup}>
                      <span className={styles.currencyPrefix}>{formData.cash_currency}</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        id="edit-offer-in-kind-value"
                        name="value"
                        value={formData.value}
                        onChange={handleChange}
                        disabled={isLocked}
                        placeholder={t('create.form.in_kind_value_placeholder')}
                        className={errors.value ? 'error' : ''}
                      />
                    </div>
                  </FormField>
                </div>
              )}

              <div>
                <FormField id="edit-offer-quantity" label={t('create.form.creators_label')} error={errors.quantity}>
                  <Input
                    type="number"
                    min="1"
                    max={maxCreators > 0 ? maxCreators : undefined}
                    id="edit-offer-quantity"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    disabled={isLocked || quantityLockedByPayment}
                    placeholder={t('create.form.creators_placeholder')}
                    className={errors.quantity ? 'error' : ''}
                  />
                </FormField>
                {maxCreators > 0 && (
                  <span className="v2-field-help">
                    {t('create.form.creators_limit_hint', { count: maxCreators, defaultValue: `Tu plan permite hasta ${maxCreators} creadores por collab.` })}
                  </span>
                )}
                {isUnlimitedCreators && (
                  <span className="v2-field-help">
                    {t('create.form.creators_unlimited_hint', 'Tu plan permite creadores ilimitados por collab.')}
                  </span>
                )}
                {quantityLockedByPayment && (
                  <small style={{ color: '#754b00', display: 'block', marginTop: '4px' }}>
                    {t('payment_release.position_reduction_edit_help', 'El cupo y monto están protegidos porque el pago ya fue confirmado.')}
                  </small>
                )}
              </div>

              {isPaidCollab && Boolean(formData.sponsor_amount) && Number(formData.quantity) > 0 && (
                <div className={styles.colSpan2}>
                  <div className={styles.investmentCard}>
                    <div className={styles.investmentHeader}>
                      <span className={styles.investmentTitle}>{t('create.form.total_investment_title', 'Inversión Total Estimada')}</span>
                      <span className={styles.investmentAmount}>
                        {formData.cash_currency} {totalInvestment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <p className={styles.investmentDetail}>
                      {t('create.form.total_investment_calculation', {
                        count: formData.quantity,
                        amount: `${formData.cash_currency} ${Number(formData.sponsor_amount || 0).toFixed(2)}`,
                        defaultValue: `${formData.quantity} creadores × ${formData.cash_currency} ${Number(formData.sponsor_amount || 0).toFixed(2)} por creador.`
                      })}
                    </p>
                  </div>
                </div>
              )}

              <div className={styles.colSpan2}>
                <FormField id="edit-offer-dates" label={t('create.form.dates_label')} error={errors.dates}>
                  <FothDateRangePicker
                    startDate={formData.from_date}
                    endDate={formData.to_date}
                    onChange={handleDateRangeChange}
                    startLabel={t('create.form.dates_start')}
                    endLabel={t('create.form.dates_end')}
                    startPlaceholder={t('create.form.dates_start_placeholder')}
                    endPlaceholder={t('create.form.dates_end_placeholder')}
                    startError={errors.from_date}
                    endError={errors.to_date}
                    maxDays={30}
                    required={true}
                    minDate={undefined}
                    maxDate={getMaximumCampaignDate()}
                    startId="edit-offer-start-date"
                    endId="edit-offer-end-date"
                  />
                </FormField>
              </div>

              <div className={styles.colSpan2}>
                <FormField id="edit-offer-country" label={t('create.form.country_label')} error={errors.country}>
                  <Select
                    id="edit-offer-country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    disabled={isLocked}
                    className={errors.country ? 'error' : ''}
                  >
                    <option value="">{t('create.form.country_placeholder')}</option>
                    {countries.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </Select>
                </FormField>
              </div>

              <div className={styles.colSpan2}>
                <div className="v2-editor-label" id="edit-offer-description-editor-label">
                  {t('create.form.description_label')}
                </div>
                <div className={`v2-rich-text ${errors.description ? 'error' : ''}`}>
                  {isQuillLoaded ? (
                    <ReactQuill
                      id="edit-offer-description-editor"
                      labelledBy="edit-offer-description-editor-label"
                      error={errors.description}
                      value={formData.description || ''}
                      onChange={(content) => handleRichTextChange(content, 'description')}
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder={t('create.form.description_placeholder')}
                      theme="snow"
                      readOnly={isLocked}
                    />
                  ) : (
                    <div style={{ padding: '16px', color: '#a6a6a6' }}>{t('create.form.loading_editor')}</div>
                  )}
                </div>
                {errors.description && <p id="edit-offer-description-editor-error" className="v2-error">{errors.description}</p>}
              </div>

              <div className={styles.colSpan2}>
                <Switch
                  label={t('create.form.time_range_label')}
                  hint={t('create.form.time_range_help')}
                  checked={formData.add_time_range}
                  disabled={isLocked}
                  onChange={event => {
                    setIsDirty(true);
                    setFormData(previous => ({ ...previous, add_time_range: event.target.checked }));
                  }}
                />
              </div>

              <div className={styles.colSpan2}>
                <RadioGroup
                  label={t('create.form.type_label')}
                  name="collab-type"
                  value={formData.type}
                  disabled={isLocked}
                  onChange={type => {
                    setIsDirty(true);
                    setFormData(p => ({ ...p, type }));
                    if (errors.type) setErrors(prev => ({ ...prev, type: null }));
                  }}
                  options={TYPE_CHOICES.map(type => ({
                    value: type.id,
                    label: <><span aria-hidden="true">{type.icon}</span> {t(type.name)}</>
                  }))}
                />
                {errors.type && <p className="v2-error">{errors.type}</p>}
              </div>
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div key="step2" variants={stepVariants} initial="hidden" animate="visible" exit="exit">
            <div className={styles.sectionHeading}>
              <h3>{t('create.steps.requirements', 'Requisitos y Entregables')}</h3>
              <p>{t('create.steps.requirements_subtitle', 'Define el perfil de creador esperado y los formatos de contenido.')}</p>
            </div>

            <div className={styles.formGrid}>
              <div className={styles.colSpan2}>
                <RadioGroup
                  label={t('create.form.followers_label')}
                  name="collab-followers"
                  value={formData.followers_requirements}
                  disabled={isLocked}
                  onChange={followers_requirements => {
                    setIsDirty(true);
                    setFormData(p => ({ ...p, followers_requirements }));
                  }}
                  options={FOLLOWERS_REQUIREMENTS.map(req => ({ value: req.id, label: t(req.name) }))}
                />
              </div>

              <div className={styles.colSpan2}>
                <RadioGroup
                  label={t('create.form.deliverables_mode_label', 'Requisitos de Entregables')}
                  name="collab-deliverables-mode"
                  value={formData.deliverables_mode}
                  disabled={isLocked}
                  onChange={handleDeliverablesModeChange}
                  options={DELIVERABLES_MODE_OPTIONS.map(mode => ({
                    value: mode.id,
                    label: t(mode.titleKey, mode.title),
                    hint: t(mode.helpKey, mode.help)
                  }))}
                />
              </div>

              <div className={styles.colSpan2} id="edit-offer-exchange-options">
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '13px' }}>
                  {formData.deliverables_mode === 'open'
                    ? t('create.form.exchange_open_label', 'Formatos preferidos')
                    : t('create.form.exchange_label', 'Entregables requeridos')}
                </label>

                {formData.deliverables_mode === 'open' && (
                  <div className="v2-open-deliverables-note" style={{ marginBottom: '12px' }}>
                    {t('create.form.deliverables_open_note', 'Los creadores tienen libertad de proponer el formato que mejor represente la experiencia.')}
                  </div>
                )}

                <div className={`${styles.deliverablesList} v2-deliverables-list`}>
                  {exchangeOptionsList.map(opt => {
                    const optValue = getOptionValue(opt);
                    const isSelected = formData.exchange_options.includes(optValue);
                    const count = formData.deliverables_counts[optValue] ?? 1;

                    return (
                      <div
                        key={optValue || opt.id || opt.code}
                        className={`${styles.deliverableRow} v2-deliverable-item ${isSelected ? styles.selected : ''}`}
                      >
                        <button
                          type="button"
                          className={styles.deliverableToggle}
                          onClick={() => !isLocked && handleMultiSelect('exchange_options', optValue)}
                          disabled={isLocked}
                          aria-pressed={isSelected}
                        >
                          <span className={`${styles.checkCircle} ${isSelected ? styles.checked : ''}`}>
                            {isSelected && <FaCheck />}
                          </span>
                          <span>{t(`create.form.exchange_options.${optValue}`, opt.name)}</span>
                        </button>

                        <AnimatePresence>
                          {isSelected && formData.deliverables_mode === 'defined' && (
                            <motion.div
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.9 }}
                              className={`${styles.stepperCounter} v2-deliverable-counter`}
                            >
                              <button
                                type="button"
                                className={styles.stepperBtn}
                                onClick={() => handleDeliverableCountIncrement(optValue, -1)}
                                disabled={isLocked || count <= 1}
                                aria-label="Disminuir cantidad"
                              >
                                <FaMinus size={10} />
                              </button>
                              <input
                                id={`edit-deliverable-${optValue}`}
                                type="number"
                                min="1"
                                value={count}
                                disabled={isLocked}
                                onChange={(e) => handleDeliverableCountChange(optValue, e.target.value)}
                              />
                              <button
                                type="button"
                                className={styles.stepperBtn}
                                onClick={() => handleDeliverableCountIncrement(optValue, 1)}
                                disabled={isLocked}
                                aria-label="Aumentar cantidad"
                              >
                                <FaPlus size={10} />
                              </button>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })}
                </div>
                {errors.exchange_options && <p className="v2-error">{errors.exchange_options}</p>}
                {errors.deliverables_counts && <p className="v2-error">{errors.deliverables_counts}</p>}
              </div>

              <div className={styles.colSpan2}>
                <fieldset className="v2-form-group-reset">
                  <legend style={{ display: 'block', marginBottom: '8px', fontWeight: 600, fontSize: '13px' }}>
                    {t('create.form.target_label')}
                  </legend>
                  <div className={`${styles.pillGroup} v2-pill-group`}>
                    {targetOptionsList.map(opt => {
                      const optValue = getOptionValue(opt);
                      const isSelected = formData.target_options.includes(optValue);

                      return (
                        <button
                          type="button"
                          key={optValue || opt.id || opt.code}
                          disabled={isLocked}
                          className={`${styles.nichePill} v2-pill ${isSelected ? 'selected' : ''} ${isSelected ? styles.selected : ''}`}
                          onClick={() => handleMultiSelect('target_options', optValue)}
                        >
                          {isSelected && <FaCheck size={11} />}
                          <span>{t(`create.form.target_options.${optValue}`, opt.name)}</span>
                        </button>
                      );
                    })}
                  </div>
                </fieldset>
                {errors.target_options && <p className="v2-error">{errors.target_options}</p>}
              </div>
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div key="step3" variants={stepVariants} initial="hidden" animate="visible" exit="exit">
            <div className={styles.sectionHeading}>
              <h3>{t('create.steps.media', 'Media y Términos')}</h3>
              <p>{t('create.steps.media_subtitle', 'Sube los recursos visuales y especifica las condiciones de la colaboración.')}</p>
            </div>

            <div className={styles.formGrid}>
              <div className={styles.colSpan2}>
                {featuredImagePreview && (
                  <div className={styles.imagePreviewGrid}>
                    <div className={styles.imageThumb} style={{ width: '120px', height: '120px' }}>
                      <img src={featuredImagePreview} alt="Featured" />
                      {!isLocked && (
                        <button
                          type="button"
                          aria-label={t('common:ui.removeImage', 'Eliminar imagen')}
                          onClick={() => handleRemoveImage(null, 'featured')}
                          className={styles.removeImageBtn}
                        >
                          <FaTrash size={11} />
                        </button>
                      )}
                    </div>
                  </div>
                )}
                <FormField id="edit-featuredImageInput" label={t('create.form.featured_image_label')} hint={t('create.form.featured_image_help', 'Formatos permitidos: JPG, PNG')} error={errors.featured_image}>
                  <Input
                    id="edit-featuredImageInput"
                    type="file"
                    disabled={isLocked}
                    onChange={e => handleImageChange(e, 'featured')}
                    accept="image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png"
                  />
                </FormField>
              </div>

              <div className={styles.colSpan2}>
                {additionalImagesPreview.length > 0 && (
                  <div className={styles.imagePreviewGrid}>
                    {additionalImagesPreview.map((src, i) => (
                      <div key={i} className={styles.imageThumb}>
                        <img src={src} alt={`Additional ${i + 1}`} />
                        {!isLocked && (
                          <button
                            type="button"
                            aria-label={`${t('common:ui.removeImage', 'Eliminar')} ${i + 1}`}
                            onClick={() => handleRemoveImage(i, 'additional')}
                            className={styles.removeImageBtn}
                          >
                            <FaTrash size={10} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                <FormField id="edit-additionalImagesInput" label={t('create.form.additional_images_label')} hint={t('create.form.additional_images_hint', 'Máx. 5 imágenes (JPG, PNG)')}>
                  <Input
                    id="edit-additionalImagesInput"
                    type="file"
                    multiple
                    disabled={isLocked || additionalImagesPreview.length >= 5}
                    onChange={e => handleImageChange(e, 'additional')}
                    accept="image/jpeg,image/png,image/jpg,.jpg,.jpeg,.png"
                  />
                </FormField>
              </div>

              <div className={styles.colSpan2}>
                <div className="v2-editor-label" id="edit-offer-terms-editor-label">
                  {t('create.form.terms_label')}
                </div>
                <div className={`v2-rich-text ${errors.terms_and_conditions ? 'error' : ''}`} style={{ minHeight: '180px' }}>
                  <ReactQuill
                    id="edit-offer-terms-editor"
                    labelledBy="edit-offer-terms-editor-label"
                    error={errors.terms_and_conditions}
                    value={formData.terms_and_conditions}
                    onChange={(content) => handleRichTextChange(content, 'terms_and_conditions')}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder={t('create.form.terms_placeholder')}
                    theme="snow"
                    readOnly={isLocked}
                  />
                </div>
                {errors.terms_and_conditions && <p id="edit-offer-terms-editor-error" className="v2-error">{errors.terms_and_conditions}</p>}
              </div>
            </div>
          </motion.div>
        );

      default: return null;
    }
  };

  if (loading) {
    return (
      <div className={styles.page} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <div className="loading-spinner" />
      </div>
    );
  }

  if (!isAuthenticated || currentUser?.user_type !== 'business') {
    return (
      <div className={styles.page} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <p>{t('create.actions.login_business')}</p>
      </div>
    );
  }

  if (!verificationStatus?.is_verified) {
    return <VerificationRequired />;
  }

  const isDraft = originalStatus === 'draft';

  return (
    <div className={styles.page}>
      <AICampaignAssistant
        isOpen={isAIAssistantOpen}
        onClose={() => setIsAIAssistantOpen(false)}
        onApplyData={applyAIAssistantData}
        currentFormData={formData}
        mode="edit"
        compensationMode={formData.compensation_mode || 'exchange'}
        countries={countries}
      />

      <header className={styles.topbar}>
        <div className={styles.topbarBrand}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => navigate(`/business/collabs/${id}/summary`)}
          >
            <FaArrowLeft /> {t('summary.back', 'Volver')}
          </button>

          <div className={styles.collabIdentity}>
            <span className={styles.identityTitle}>{formData.title || t('summary.actions.edit')}</span>
            <div className={styles.identityMeta}>
              <span>{originalStatus === 'active' ? t('common.active', 'Activa') : t('common.draft', 'Borrador')}</span>
              <span>·</span>
              <span>{isPaidCollab ? 'Paid Collab' : 'Exchange'}</span>
            </div>
          </div>
        </div>

        <div className={styles.topbarCenter}>
          <div className={styles.stepperTrack}>
            <div
              className={styles.stepperFill}
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            />
          </div>
          <span className={styles.stepperLabel}>
            Paso {currentStep} de {totalSteps} · <strong>
              {currentStep === 1 ? t('create.steps.details', 'Detalles') : currentStep === 2 ? t('create.steps.requirements', 'Requisitos') : t('create.steps.media', 'Media')}
            </strong>
          </span>
        </div>

        <div className={styles.topbarActions}>
          {!isLocked && (
            <button
              type="button"
              className={styles.aiButton}
              onClick={() => setIsAIAssistantOpen(true)}
            >
              <FaMagic /> {t('create.ai_btn', 'FOTH AI')}
            </button>
          )}

          {!isLocked && currentStep < totalSteps && (
            <button
              type="button"
              className={styles.quickSaveBtn}
              onClick={() => submitOfferData(isDraft ? 'draft' : 'save')}
              disabled={submitting || isPublishing}
            >
              <FaSave /> {t('summary.actions.save_changes', 'Guardar')}
            </button>
          )}
        </div>
      </header>

      <main className={styles.container}>
        {renderProgressBar()}

        {isLocked && (
          <div className={styles.lockAlert}>
            <FaLock size={18} className={styles.lockIcon} />
            <div className={styles.lockText}>
              <strong>{t('summary.actions.edit_locked_title', 'Edición de colaboración protegida')}</strong>
              <p>
                {t('summary.actions.edit_locked_help', 'Esta colaboración ya ha sido financiada o cuenta con entregables de creadores en curso. Por seguridad del acuerdo y cumplimiento contractual, sus términos principales no pueden ser alterados.')}
              </p>
            </div>
          </div>
        )}

        <div className={styles.card}>
          <form onSubmit={(e) => e.preventDefault()}>
            <AnimatePresence mode="wait">
              {renderFormStep()}
            </AnimatePresence>

            {currentStep === totalSteps && (
              <div className={styles.fairnessNotice}>
                {t('create.fairness_warning')}
              </div>
            )}

            <FormErrorSummary
              errors={errors}
              currentStep={currentStep}
              prefix="edit-offer"
              onFieldClick={(fieldKey) => scrollToAndFocusField(fieldKey, 'edit-offer')}
              t={t}
            />

            <footer className={styles.formActions}>
              <Button
                variant="outline"
                type="button"
                onClick={handlePreviousStep}
                disabled={currentStep === 1 || submitting || isPublishing}
              >
                <FaArrowLeft /> {t('create.actions.prev')}
              </Button>

              <div className={styles.actionsRight}>
                {currentStep < totalSteps ? (
                  <>
                    <Button
                      variant="outline"
                      type="button"
                      onClick={() => submitOfferData(isDraft ? 'draft' : 'save')}
                      disabled={submitting || isPublishing}
                      aria-disabled={isLocked}
                    >
                      <FaSave /> {isDraft ? t('summary.actions.revert_draft', 'Guardar borrador') : t('summary.actions.save_changes', 'Guardar cambios')}
                    </Button>
                    <Button
                      variant="primary"
                      type="button"
                      onClick={handleNextStep}
                    >
                      {t('create.actions.next')}
                    </Button>
                  </>
                ) : (
                  <>
                    {isDraft ? (
                      <>
                        <Button
                          variant="outline"
                          type="button"
                          onClick={() => submitOfferData('draft')}
                          disabled={submitting || isPublishing}
                          aria-disabled={isLocked}
                        >
                          {submitting ? t('create.actions.saving') : <><FaSave /> {t('summary.actions.revert_draft')}</>}
                        </Button>
                        <Button
                          variant="primary"
                          type="button"
                          onClick={() => submitOfferData('active')}
                          disabled={submitting || isPublishing}
                          aria-disabled={isLocked}
                        >
                          {isPublishing ? t('create.actions.publishing') : <><FaPaperPlane /> {t('create.actions.publish')}</>}
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="primary"
                        type="button"
                        onClick={() => submitOfferData('save')}
                        disabled={submitting || isPublishing}
                        aria-disabled={isLocked}
                      >
                        {submitting ? t('create.actions.saving') : <><FaSave /> {t('summary.actions.save_changes')}</>}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </footer>
          </form>
        </div>
      </main>
    </div>
  );
};

export default EditOffer;
